import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TextInput, TouchableOpacity } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { Icon, IconName } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';
import { PRODUCTS, Product, ProductTarget } from '../data/products';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M04ProductListScreen'>;

type CategoryId = 'all' | 'huu_tri' | 'giao_duc' | 'tiet_kiem' | 'bao_ve' | 'suc_khoe';

interface Category {
  id: CategoryId;
  label: string;
  icon: IconName;
}

const CATEGORIES: Category[] = [
  { id: 'all', label: 'Tất cả', icon: 'category' },
  { id: 'huu_tri', label: 'Hưu trí', icon: 'elderly' },
  { id: 'giao_duc', label: 'Giáo dục', icon: 'school' },
  { id: 'tiet_kiem', label: 'Tiết kiệm', icon: 'savings' },
  { id: 'bao_ve', label: 'Bảo vệ Tài Chính', icon: 'shield' },
  { id: 'suc_khoe', label: 'Chăm sóc sức khỏe', icon: 'health_and_safety' },
];

const TARGET_LABEL: Record<ProductTarget, string> = {
  'life-protection': 'Bảo vệ',
  'education': 'Giáo dục',
  'retirement': 'Hưu trí',
  'savings': 'Tiết kiệm',
};

const TARGET_ICON: Record<ProductTarget, IconName> = {
  'life-protection': 'shield',
  'education': 'school',
  'retirement': 'elderly',
  'savings': 'savings',
};

const TARGET_COLOR: Record<ProductTarget, string> = {
  'life-protection': colors.primary,
  'education': colors.tertiary,
  'retirement': '#6F42C1',
  'savings': colors.secondary,
};

const RISK_TOOLTIP_TEXT =
  'Yêu cầu hoàn thành Bảng câu hỏi đánh giá mức độ chấp nhận rủi ro trong đầu tư';

export const M04ProductListScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<CategoryId>('all');
  const [tooltipProductId, setTooltipProductId] = useState<string | null>(null);

  const filteredProducts = PRODUCTS.filter((product) => {
    const q = search.toLowerCase();
    const matchesSearch =
      product.name.toLowerCase().includes(q) ||
      product.tagline.toLowerCase().includes(q) ||
      product.fullName.toLowerCase().includes(q) ||
      product.features.some((f) => f.toLowerCase().includes(q));

    let matchesTab = true;
    if (activeTab !== 'all' && activeTab !== 'suc_khoe') {
      const targetMap: Record<Exclude<CategoryId, 'all' | 'suc_khoe'>, ProductTarget> = {
        huu_tri: 'retirement',
        giao_duc: 'education',
        tiet_kiem: 'savings',
        bao_ve: 'life-protection',
      };
      matchesTab = product.targets.includes(targetMap[activeTab]);
    } else if (activeTab === 'suc_khoe') {
      matchesTab = false;
    }

    return matchesSearch && matchesTab;
  });

  const handleChoose = (product: Product) => {
    navigation.navigate('M05ProductDetailScreen', { productId: product.id });
  };

  const renderProductCard = (product: Product) => {
    const showTooltip = product.requireRiskAssessment && tooltipProductId === product.id;
    const ctaLabel = product.isDefault ? 'Tạo TLMHBH' : 'Chọn gói này';
    const ctaIsRed = product.isDefault;
    return (
      <TouchableOpacity
        key={product.id}
        style={styles.card}
        activeOpacity={0.95}
        onPress={() => handleChoose(product)}
      >
        <View style={styles.banner}>
          <View style={styles.bannerOverlay} />
          <View style={styles.bannerContent}>
            <View style={styles.bannerIconWrap}>
              <Icon name="shield" size={24} color={colors.primary} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.bannerTitle} numberOfLines={1}>
                {product.name}
              </Text>
              <Text style={styles.bannerSubtitle} numberOfLines={1}>
                {product.typeLabel}
              </Text>
            </View>
          </View>
          {product.requireRiskAssessment && (
            <TouchableOpacity
              style={styles.tooltipBtn}
              onPress={() =>
                setTooltipProductId(tooltipProductId === product.id ? null : product.id)
              }
              accessibilityLabel="Cảnh báo rủi ro"
            >
              <Icon name="info" size={18} color={colors.primary} />
            </TouchableOpacity>
          )}
        </View>


        {showTooltip && (
          <View style={styles.tooltipBox}>
            <View style={styles.tooltipArrow} />
            <Text style={styles.tooltipText}>{RISK_TOOLTIP_TEXT}</Text>
          </View>
        )}

        <View style={styles.cardBody}>
          <Text style={styles.tagline}>{product.tagline}</Text>

          {product.features.length > 0 && (
            <View style={styles.featuresList}>
              {product.features.map((feat, i) => (
                <View key={i} style={styles.featureItem}>
                  <View style={styles.featureDot} />
                  <Text style={styles.featureText}>{feat}</Text>
                </View>
              ))}
            </View>
          )}

          <View style={styles.targetsRow}>
            {product.targets.map((t) => (
              <View key={t} style={styles.targetItem}>
                <View style={[styles.targetIconWrap, { backgroundColor: TARGET_COLOR[t] + '15' }]}>
                  <Icon name={TARGET_ICON[t]} size={20} color={TARGET_COLOR[t]} />
                </View>
                <Text style={styles.targetLabel}>{TARGET_LABEL[t]}</Text>
              </View>
            ))}
          </View>

          <TouchableOpacity
            style={[styles.chooseBtn, ctaIsRed && styles.chooseBtnRed]}
            onPress={() => handleChoose(product)}
          >
            <Text style={[styles.chooseBtnText, ctaIsRed && styles.chooseBtnTextRed]}>
              {ctaLabel}
            </Text>
            <Icon
              name="arrow_forward"
              size={18}
              color={ctaIsRed ? '#ffffff' : colors.primary}
            />

          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      {/* Fixed Header: Search + Filter Chips + Section Title */}
      <View style={styles.fixedHeader}>
        {/* Search Bar */}
        <View style={styles.searchContainer}>
          <View style={styles.searchWrapper}>
            <Icon name="search" size={20} color={colors.onSurfaceVariant} style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder="Tìm kiếm giải pháp bảo vệ..."
              placeholderTextColor={colors.onSurfaceVariant}
              value={search}
              onChangeText={setSearch}
            />
            {search.length > 0 && (
              <TouchableOpacity onPress={() => setSearch('')}>
                <Icon name="close" size={20} color={colors.onSurfaceVariant} />
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Filter Chips */}
        <View style={styles.tabContainer}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.tabScroll}
          >
            {CATEGORIES.map((cat) => {
              const isActive = activeTab === cat.id;
              return (
                <TouchableOpacity
                  key={cat.id}
                  style={[styles.tabButton, isActive && styles.tabActive]}
                  onPress={() => setActiveTab(cat.id)}
                  activeOpacity={0.8}
                >
                  {isActive && <Icon name={cat.icon} size={14} color="#ffffff" style={styles.tabIcon} />}
                  <Text style={[styles.tabText, isActive && styles.tabTextActive]}>
                    {cat.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>

        {/* Section Title */}
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>
            {activeTab === 'all'
              ? 'Tất cả sản phẩm'
              : CATEGORIES.find((c) => c.id === activeTab)?.label}
          </Text>
          <Text style={styles.sectionCount}>{filteredProducts.length} sản phẩm</Text>
        </View>
      </View>

      {/* Scrollable Products List */}
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.listContainer}>
          {filteredProducts.map(renderProductCard)}

          {filteredProducts.length === 0 && (
            <View style={styles.emptyContainer}>
              <Icon name="search" size={48} color={colors.onSurfaceVariant} />
              <Text style={styles.emptyText}>Không tìm thấy sản phẩm phù hợp</Text>
              <Text style={styles.emptyHint}>
                {activeTab === 'suc_khoe'
                  ? 'Hiện chưa có sản phẩm thuộc danh mục này'
                  : 'Thử từ khóa khác hoặc chọn danh mục khác'}
              </Text>
              {activeTab === 'suc_khoe' && (
                <TouchableOpacity style={styles.emptyCta} onPress={() => setActiveTab('all')}>
                  <Text style={styles.emptyCtaText}>Xem tất cả sản phẩm</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  fixedHeader: {
    backgroundColor: colors.background,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 4,
    elevation: 2,
    zIndex: 10,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: spacing[8],
    flexGrow: 1,
  },
  searchContainer: {
    paddingHorizontal: spacing[4],
    paddingTop: spacing[3],
    paddingBottom: spacing[3],
    backgroundColor: colors.background,
  },
  searchWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing[4],
    backgroundColor: '#ffffff',
    height: 48,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 1,
  },
  searchIcon: {
    marginRight: spacing[2],
  },
  searchInput: {
    flex: 1,
    height: '100%',
    color: colors.onSurface,
    fontSize: 15,
    paddingVertical: 0,
  },
  tabContainer: {
    paddingVertical: spacing[2],
    backgroundColor: colors.background,
  },
  tabScroll: {
    paddingHorizontal: spacing[4],
    gap: spacing[2],
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2],
    borderRadius: 9999,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  tabActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 2,
  },
  tabIcon: {
    marginRight: spacing[1],
  },
  tabText: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.3,
    color: colors.onSurfaceVariant,
  },
  tabTextActive: {
    color: '#ffffff',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    paddingHorizontal: spacing[4],
    marginTop: spacing[3],
    marginBottom: spacing[2],
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  sectionCount: {
    fontSize: 12,
    fontWeight: fontWeights.semibold,
    color: colors.onSurfaceVariant,
  },
  listContainer: {
    paddingHorizontal: spacing[4],
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[4],
    justifyContent: 'center',
    paddingBottom: spacing[6],
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
    minWidth: 280,
    maxWidth: 340,
    flex: 1,
  },

  banner: {
    height: 72,
    backgroundColor: '#f9fafb',
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing[4],
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
  },
  bannerOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'transparent',
  },
  bannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    flex: 1,
  },
  bannerIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#e6f0fa',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bannerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.primary,
    letterSpacing: -0.2,
  },
  bannerSubtitle: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
  tooltipBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#e6f0fa',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: spacing[2],
  },
  tooltipBox: {
    backgroundColor: '#FFF8E1',
    borderLeftWidth: 3,
    borderLeftColor: '#FFB300',
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[3],
  },
  tooltipArrow: {
    position: 'absolute',
    top: -6,
    right: 20,
    width: 12,
    height: 12,
    backgroundColor: '#FFF8E1',
    borderLeftWidth: 3,
    borderTopWidth: 3,
    borderLeftColor: '#FFB300',
    borderTopColor: '#FFB300',
    transform: [{ rotate: '45deg' }],
  },
  tooltipText: {
    fontSize: 12,
    lineHeight: 17,
    color: '#7A5500',
  },
  cardBody: {
    padding: spacing[4],
    gap: spacing[3],
  },
  tagline: {
    fontSize: 14,
    fontWeight: fontWeights.semibold,
    color: colors.secondary,
  },
  featuresList: {
    gap: spacing[2],
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[2],
  },
  featureDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.secondary,
    marginTop: 7,
  },
  featureText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 19,
    color: colors.onSurface,
  },
  targetsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: spacing[2],
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.outlineVariant,
  },
  targetItem: {
    alignItems: 'center',
    gap: 4,
    flex: 1,
  },
  targetIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  targetLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.onSurfaceVariant,
  },
  chooseBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[1],
    paddingVertical: spacing[3],
    borderRadius: borderRadius.DEFAULT,
    borderWidth: 1.5,
    borderColor: colors.primary,
    backgroundColor: 'transparent',
  },
  chooseBtnRed: {
    borderColor: '#EF4444',
    backgroundColor: '#EF4444',
  },
  chooseBtnText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  chooseBtnTextRed: {
    color: '#ffffff',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing[10],
    gap: spacing[3],
  },
  emptyText: {
    fontSize: 15,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  emptyHint: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    textAlign: 'center',
  },
  emptyCta: {
    marginTop: spacing[2],
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[3],
    borderRadius: borderRadius.DEFAULT,
    backgroundColor: colors.primary,
  },
  emptyCtaText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },
});
